import React, { useEffect, useRef, useState } from "react";
import '../../assets/styles/emailverification.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { commonService } from "../../services/otherServices";
import Pagination from "../../components/Common/Pagination";
import Popup from "../../components/Common/Error";
import axios, { AxiosError } from "axios";


const SearchPage: React.FC = () => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [fileName, setFileName] = useState<string>("");
    const [email, setEmail] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [status, setStatus] = useState("Verifying...");
    const [details, setDetails] = useState("--");
    const [domain, setDomain] = useState("--");
    const [isLoading, setIsLoading] = useState(false);
    const [isBulkLoading, setIsBulkLoading] = useState(false);
    const [isDownloadLoading, setDownloadLoading] = useState(0);
    const pollingRef = useRef<NodeJS.Timeout | null>(null);
    const [downloads, setDownloads] = useState<any[]>([]);
    const [pageDetail, setPageDetail] = useState({
        page: 1,
        total_pages: 1,
        total_records: 0,
        page_size: 5,
    });

    const [listLoading, setListLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [getMoreCredit, setGetMoreCredit] = useState("");
    const [creditDetail, setCreditDetail] = useState<any>(null);
    const isButtonDisabled = !fileName;

    const [requestCreditsLoading, setRequestCreditsLoading] = useState(false);
    const [showCreditPopup, setShowCreditPopup] = useState(false);
    const [showCreditForm, setShowCreditForm] = useState(false);
    const [credit, setCredit] = useState("");


    const isValidEmail = (email: string) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const setLocalStorageWithEvent = (key: string, value: string) => {
        localStorage.setItem(key, value);
        // Dispatch custom event for same tab
        window.dispatchEvent(new CustomEvent('localStorageChange', {
            detail: { key, value }
        }));
    };

    const isDisabled = !isValidEmail(email);

    const countEmailsInCSV = (file: File): Promise<number> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (event) => {
                try {
                    const content = event.target?.result as string;
                    const lines = content.split('\n');

                    // Check if file has content
                    if (lines.length <= 1) {
                        resolve(0);
                        return;
                    }

                    let emailCount = 0;

                    // Start from index 1 to skip header row (assuming first line is header)
                    for (let i = 1; i < lines.length; i++) {
                        const line = lines[i].trim();
                        if (line) {
                            // Get the first column (assuming it's email)
                            const email = line.split(',')[0]?.trim();
                            // Basic email validation - check if contains @
                            if (email && email.includes('@')) {
                                emailCount++;
                            }
                        }
                    }

                    resolve(emailCount);

                } catch (error) {
                    reject(new Error('Error parsing CSV file'));
                }
            };

            reader.onerror = () => {
                reject(new Error('Error reading file'));
            };

            reader.readAsText(file);
        });
    };

    const handleStartVerification = async () => {

        if (!fileInputRef.current?.files?.[0]) return;
        const file = fileInputRef.current.files[0];
        if (!file.name.toLowerCase().endsWith('.csv')) {
            setErrorMsg("Please upload a CSV file");
            setFileName('');
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            setIsBulkLoading(false);
            return;
        }
        try {
            setIsBulkLoading(true);
            const emailCount = await countEmailsInCSV(file);

            if (emailCount === 0) {
                setErrorMsg("No valid email addresses found in the CSV file. Please ensure your CSV has email addresses.");
                setFileName('');
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
                setIsBulkLoading(false);
                return;
            }

            const data = await fetchCreditDetails();
            if (data.available_credits <= emailCount) {
                const available = data.available_credits || 0;
                setFileName('');
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
                setIsBulkLoading(false);
                if (available === 0) {
                    setGetMoreCredit(
                        `Insufficient credits to perform email verification.`);
                    return;
                } else {
                    setGetMoreCredit(
                        `You have uploaded ${emailCount} emails, but only ${available} credits are available. ` +
                        `Please reduce your upload to ${available} emails or purchase more credits to continue.`
                    );
                    return;
                }

            }

            const payload = { file: fileInputRef.current?.files?.[0] };
            const response = await commonService.bulkEmailVerify(payload);

            setShowBulkModal(true);
            const body = { credits: emailCount };
            const res = await commonService.debitCredit(body);
            const creditData = {
                available_credits: Number(res?.data?.available_credits) || 0,
                total_credits: Number(res?.data?.total_credits) || 0,
                used_credits: Number(res?.data?.used_credits) || 0
            };

            setCreditDetail(creditData);
            setLocalStorageWithEvent('credit', JSON.stringify(creditData));
            loadPage(1);
        } catch (err) {
            if (axios.isAxiosError(err)) {
                const errorData = err.response?.data;

                if (errorData?.details?.length) {
                    setErrorMsg(errorData.details[0]?.input?.message);
                } else if (errorData?.message) {
                    setErrorMsg(errorData.message);
                } else {
                    setErrorMsg("Something went wrong");
                }
            } else {
                setErrorMsg("Unexpected error occurred");
            }
        } finally {
            setFileName('');
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            setIsBulkLoading(false);
        }

    };

    const closeBulkModal = () => {
        setShowBulkModal(false);
        setFileName('');
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const fetchCreditDetails = async () => {
        try {
            const response = await commonService.getCreditDetails();
            // console.log("Credit Details:", response.data);

            // Validate the response data exists
            if (response && response.data) {
                // Ensure numeric values and set defaults
                const creditData = {
                    available_credits: Number(response?.data?.available_credits) || 0,
                    total_credits: Number(response?.data?.total_credits) || 0,
                    used_credits: Number(response?.data?.used_credits) || 0
                };

                setCreditDetail(creditData);
                setLocalStorageWithEvent('credit', JSON.stringify(creditData));
            } else {
                // Set default values if response is invalid
                setCreditDetail({
                    available_credits: 0,
                    total_credits: 0,
                    used_credits: 0
                });
            }

            return response?.data;
        } catch (error) {
            // console.error("Failed to fetch credit details:", error);
            // Set default values on error
            setCreditDetail({
                available_credits: 0,
                total_credits: 0,
                used_credits: 0
            });
            // setErrorMsg("Failed to fetch credit details.");
        }
    };

    const handleVerify = async () => {

        try {
            setShowPopup(true);
            setIsLoading(true);
            const data = await fetchCreditDetails();
            // Check available credits
            if (data.available_credits <= 0) {
                setShowPopup(false);
                setIsLoading(false);
                setGetMoreCredit("Insufficient credits to perform email verification.");
                return;
            }
            const payload = { email };
            const response = await commonService.singleEmailVerify(payload);
            if (response?.data?.id) {
                const body = { credits: 1 };
                const res = await commonService.debitCredit(body);
                const creditData = {
                    available_credits: Number(res?.data?.available_credits),
                    total_credits: Number(res?.data?.total_credits),
                    used_credits: Number(res?.data?.used_credits)
                };

                setCreditDetail(creditData);
                setLocalStorageWithEvent('credit', JSON.stringify(creditData));
                pollVerificationResult(response.data.id);
            }

        } catch (error) {
            setStatus("Failed");
            setIsLoading(false);
        }
    };

    const closePopup = () => {
        setShowPopup(false);
        setIsLoading(false);
        setStatus("Verifying...");
        setDetails("--")
        setEmail("");
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
        }
    };
    const getDomain = () => {
        if (!email || !email.includes('@')) return '';
        return email.split('@')[1];
    };
    const pollVerificationResult = (id: string) => {
        pollingRef.current = setInterval(async () => {
            try {
                const res = await commonService.getSingleEmailResult(id);

                if (res?.data?.status && res.data.status == "completed") {
                    setStatus(res.data.result[0].status);
                    setDetails(res.data.result[0]?.substatus || "No additional sub-status available");

                    setDomain((getDomain()));
                    setIsLoading(false);
                    if (pollingRef.current) {
                        clearInterval(pollingRef.current);
                    }
                } else if (res?.data?.status && res.data.status == "failed") {
                    setStatus("Failed");
                    setIsLoading(false);
                    if (pollingRef.current) {
                        clearInterval(pollingRef.current);
                    }
                }
            } catch (err) {
                setStatus("Failed");
                setIsLoading(false);
                if (pollingRef.current) {
                    clearInterval(pollingRef.current);
                }
                // console.error("Polling failed", err);
            }
        }, 5000);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFileName(e.target.files[0].name);
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            setFileName(e.dataTransfer.files[0].name);
        }
    };

    useEffect(() => {
        loadPage(1);
    }, []);

    const loadPage = async (page: number, silent = false) => {
        if (listLoading && !silent) return;

        try {
            if (!silent) {
                setListLoading(true);
            }

            const payload = {
                page,
                page_size: pageDetail.page_size,
            };

            const response = await commonService.getEmailVerifyList(payload);

            setDownloads(response.data.items || []);

            setPageDetail({
                page: response.data.page,
                total_pages: response.data.total_pages,
                total_records: response.data.total,
                page_size: response.data.page_size,
            });
        } catch (err) {
            let message = '';
            if (axios.isAxiosError(err)) {
                const errorData = err.response?.data;

                if (errorData?.details?.length) {
                    message = errorData.details[0]?.input?.message;
                } else if (errorData?.message) {
                    message = errorData.message;
                } else {
                    message = "Something went wrong";
                }
            } else {
                message = "Unexpected error occurred";
            }
            if (!silent) {
                setErrorMsg(message);
            }
        } finally {
            if (!silent) {
                setListLoading(false);
            }
        }
    };

    useEffect(() => {
        if (!hasRunningJobs(downloads)) return;

        const interval = setInterval(() => {
            loadPage(pageDetail.page, true);
        }, 10000); // 10 seconds

        return () => clearInterval(interval);
    }, [downloads, pageDetail.page]);

    const downloadResult = async (item: any) => {
        if (isDownloadLoading) return;
        try {
            setDownloadLoading(item.id);
            const response = await commonService.downloadBulkEmailResult(item.id);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.download = item.file_name || "email_verification_result.csv";
            link.click();
            // Cleanup
            window.URL.revokeObjectURL(url);
        } catch (err) {
            if (axios.isAxiosError(err)) {
                const errorData = err.response?.data;

                if (errorData?.details?.length) {
                    setErrorMsg(errorData.details[0]?.input?.message);
                } else if (errorData?.message) {
                    setErrorMsg(errorData.message);
                } else {
                    setErrorMsg("Something went wrong");
                }
            } else {
                setErrorMsg("Unexpected error occurred");
            }
        } finally {
            setDownloadLoading(0);
        }
    };

    const downloadsample = async () => {
        const csvContent = [
            "email",
            "john.doe@example.com",
            "jane.smith@example.com",
            "info@testcompany.com",
            "support@mydomain.org",
            "contact@sample.co"
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = "sample.csv";

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const hasRunningJobs = (items: any[]) =>
        items.some(
            item =>
                item.status?.toLowerCase() === "pending" ||
                item.status?.toLowerCase() === "inprogress"
        );

    const submitRequest = async (credits: any) => {
        try {
            setRequestCreditsLoading(true);
            const payload = { no_of_credits: credits };
            const response = await commonService.submitCreditRequest(payload);
            setShowCreditForm(false);
            setShowCreditPopup(true);
            setCredit("");
        } catch (err) {
            if (axios.isAxiosError(err)) {
                const errorData = err.response?.data;

                if (errorData?.details?.length) {
                    setErrorMsg(errorData.details[0]?.input?.message);
                } else if (errorData?.message) {
                    setErrorMsg(errorData.message);
                } else {
                    setErrorMsg("Something went wrong");
                }
            } else {
                setErrorMsg("Failed to fetch credit details.");
            }
        } finally {
            setRequestCreditsLoading(false);
        }
    };

    return (
        <>
            <div className="container">
                <div className="email-verification-container">
                    <h3 className="verification-heading">Email Verification</h3>
                    <p className="verification-text">Ensure your email lists are accurate, valid, and ready for outreach.</p>
                    <div className="d-flex flex-row justify-content-center">
                        <><div className="white-verification-box">
                            <div className="d-flex flex-row">
                                <div>
                                    <img src="images/singl-email-verifire.png" width="30" alt="" />
                                </div>
                                <div>
                                    <p className="sigle-header">Single Email Verifier</p>
                                </div>
                            </div>

                            <label className="verification-label">
                                Enter an email address to verify its validity instantly.
                            </label>

                            <input
                                type="email"
                                className="verification-input"
                                placeholder="Enter email address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />

                            <div className="d-flex flex-row justify-content between">
                                <div>
                                    <p className="Returns-sigle-header">
                                        Returns: Valid, Risky, Catch-All, Invalid, Unknown.
                                    </p>
                                </div>

                            </div>
                            <div className="d-flex flex-row justify-content-end mt-3 mb-3">
                                <div>
                                    <button disabled={!email} className="cancel" onClick={() => setEmail("")} >
                                        <span style={{ fontWeight: 500 }}> Clear</span>
                                    </button>
                                </div>
                                <div>
                                    <button
                                        className="verify-button"
                                        style={{
                                            backgroundColor: isDisabled ? "#979797" : "",
                                            cursor: isDisabled ? "not-allowed" : "pointer",
                                        }}
                                        onClick={handleVerify}
                                    >
                                        Verify
                                    </button>
                                </div>
                            </div>
                        </div>

                            {/* POPUP */}
                            {showPopup && (
                                <div className="modal-overlay" onClick={closePopup}>
                                    <div className="modal-box" onClick={(e) => e.stopPropagation()}>
                                        <div className="modal-close" onClick={closePopup}>
                                            <img src="/images/close-inactive.png" alt="Close" />
                                        </div>

                                        <h4 className="modal-title">Email Verification Result</h4>

                                        <div className="popup-box">
                                            <div className="d-flex">
                                                <p className="popup-email">Email:</p>
                                                <p className="colored-email" style={{ marginLeft: "16px" }}>{email}</p>
                                            </div>

                                            <div className="d-flex">
                                                <p className="popup-email">Status:</p>
                                                <p className="colored-email" style={{ marginLeft: "11px" }}>
                                                    {status}
                                                    {isLoading && (
                                                        <span className="spinner-border spinner-border-sm ms-2" />
                                                    )}
                                                </p>
                                            </div>

                                            <div className="d-flex">
                                                <p className="popup-email">Details:</p>
                                                <p className="colored-email" style={{ marginLeft: "8px" }}>{details}</p>
                                            </div>

                                            <div className="d-flex">
                                                <p className="popup-email">Domain:</p>
                                                <p className="colored-email">{getDomain()}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                        </>
                        <div className="white-verification-box">
                            <div className="d-flex flex-row">
                                <img src="images/singl-email-verifire.png" width="30" alt="" />
                                <p className="sigle-header">Bulk Email Verifier</p>
                            </div>

                            <label className="verification-label">
                                Upload your email list for bulk verification.
                            </label>

                            {/* DROP ZONE */}
                            <div
                                className={`csv-drop-zone ${fileName ? "active" : ""}`}
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={handleDrop}
                            >
                                <input
                                    type="file"
                                    accept=".csv"
                                    ref={fileInputRef}
                                    className="csv-input"
                                    onChange={handleFileChange}
                                />

                                <img
                                    src={fileName ? "/images/active-csv.png" : "/images/inactive-csv.png"}
                                    alt="CSV Upload"
                                    className="csv-upload-icon"
                                />

                                {fileName ? (
                                    <p className="csv-file-name">{fileName}</p>
                                ) : (
                                    <p className="csv-placeholder">
                                        Drag & drop your file here or{" "}
                                        <span className="csv-upload-link">[upload CSV]</span>
                                    </p>
                                )}
                            </div>

                            <div className="d-flex flex-row justify-content-between align-items-center">
                                <p className="Returns-sigle-header">Supported formats: CSV&nbsp;&nbsp;&nbsp;<span onClick={() => downloadsample()} style={{ color: "blue", cursor: "pointer" }} title="Click to download sample file">Download Template</span></p>

                                <button
                                    className="start-verification"
                                    style={{
                                        backgroundColor: !fileName || isLoading ? "#979797" : "",
                                        cursor: !fileName || isLoading ? "not-allowed" : "pointer",
                                    }}
                                    onClick={() => handleStartVerification()}
                                >
                                    {isBulkLoading ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                            Uploading...
                                        </>
                                    ) : (
                                        "Start Verification"
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* POPUP MODAL */}
                        {showBulkModal && (
                            <div
                                className="bulk-modal-overlay"
                                onClick={() => closeBulkModal()}
                            >
                                <div
                                    className="bulk-modal-container"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {/* CANCEL ICON */}
                                    <div
                                        className="bulk-modal-close"
                                        onClick={() => closeBulkModal()}
                                    >
                                        <img
                                            src="/images/close-inactive.png"
                                            alt="Cancel"
                                            className="close-icon-default"
                                        />
                                        <img
                                            src="/images/close-active.png"
                                            alt="Cancel"
                                            className="close-icon-hover"
                                        />
                                    </div>

                                    <div className="bulk-modal-icon">
                                        <img src="images/hand-shak.png" alt="Verification" />
                                    </div>

                                    <h4 className="bulk-modal-title">
                                        Verification in-progress
                                    </h4>

                                    <p className="bulk-modal-subtitle">
                                        You’ll be notified once the results are ready for download.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="verification-box">
                        <p className="verification-report"><img src="/images/verification-img.png" alt="" /> Verification Report</p>
                        <p className="verification-glance">Your recent verification activity at a glance.</p>

                        <div className="custom-table-wrapper" style={{ overflow: "unset" }}>
                            <table className="custom-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: "80px" }}>SL No</th>
                                        <th style={{ width: "222px" }}>File Name</th>
                                        <th>Emails Uploaded</th>
                                        <th>Emails Verified</th>
                                        <th>Status</th>
                                        <th>Download</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {downloads.length === 0 && !listLoading && (
                                        <tr>
                                            <td colSpan={6} className="text-center py-3">
                                                No verification data available.
                                            </td>
                                        </tr>
                                    )}
                                    {!listLoading && downloads.map((item, index) => (
                                        <tr key={index}>
                                            <td style={{ width: "80px" }}>{String(index + 1).padStart(2, '0')}</td>
                                            <td style={{
                                                width: "222px",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis"
                                            }}>{item.file_name}</td>
                                            <td style={{ textAlign: "center" }}>{Number(item.data_count).toLocaleString()}</td>
                                            <td style={{ textAlign: "center" }}>{Number(item.result_count).toLocaleString()}</td>
                                            <td>
                                                <div className="td-tooltip">
                                                    <span>
                                                        <img
                                                            src={`/images/${item.status.toLowerCase()}.png`}
                                                            alt=""
                                                            style={{
                                                                width: item.status?.toLowerCase() === 'completed' ? '16px' : ''
                                                            }}
                                                        />
                                                    </span>
                                                    &nbsp;
                                                    <span style={{
                                                        color:
                                                            item.status?.toLowerCase() === 'completed' ? '#0CAF60' :
                                                                item.status?.toLowerCase() === 'inprogress' ? '#787878' :
                                                                    item.status?.toLowerCase() === 'pending' ? '#000000' :
                                                                        item.status?.toLowerCase() === 'failed' ? '#c36565ff' :
                                                                            '#000000', // default color
                                                        textTransform: 'capitalize',
                                                        fontWeight: item.status?.toLowerCase() === 'failed' ? 'bold' : 'normal'
                                                    }}>
                                                        {item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1).toLowerCase() : ''}
                                                    </span>
                                                    <span className="tooltip-box">
                                                        {item.status.toLowerCase() === 'pending' && 'Awaiting verification start.'}
                                                        {item.status.toLowerCase() === 'completed' && 'Verification finished successfully.'}
                                                        {item.status.toLowerCase() === 'inprogress' && 'Verification is running.'}
                                                        {item.status.toLowerCase() === 'failed' && 'Verification failed'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                {item.status === 'completed' ? (
                                                    isDownloadLoading === item.id ? (
                                                        <span className="d-flex align-items-center text-muted">
                                                            <span
                                                                className="spinner-border spinner-border-sm me-2"
                                                                role="status"
                                                                aria-hidden="true"
                                                            />
                                                            Downloading...
                                                        </span>
                                                    ) : (
                                                        <img
                                                            src="/images/active-download.png"
                                                            alt="Download"
                                                            style={{ cursor: "pointer" }}
                                                            onClick={() => downloadResult(item)}
                                                        />
                                                    )
                                                ) : (
                                                    <img src="/images/inactive-download.png" alt="Download" />
                                                )}
                                            </td>

                                        </tr>
                                    ))}
                                    {listLoading && (
                                        <tr>
                                            <td colSpan={6} className="text-center py-3">
                                                <span className="spinner-border spinner-border-sm me-2" />
                                                Loading...
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div style={{ fontSize: "11px", textAlign: "center", fontStyle: "italic" }} className="mt-3">Accurate Data Starts with Verified Emails. Validate every contact with DataCaptive’s trusted verification engine.</div>
                        <div className="custom-pagination-wrapper">
                            <Pagination
                                page={pageDetail.page}
                                total_pages={pageDetail.total_pages}
                                total_records={pageDetail.total_records}
                                page_size={pageDetail.page_size}
                                onPageChange={(newPage) => loadPage(newPage)}
                            />
                        </div>
                    </div>
                </div>
            </div >
            <Popup
                isOpen={!!errorMsg}
                onClose={() => setErrorMsg("")}
                title={errorMsg}
                iconSrc="/images/grey-cancel-cta.png"
            />
            {getMoreCredit && (
                <div
                    className={`download-popup-overlay `}
                    onClick={() => setGetMoreCredit("")}
                >
                    <div className={`download-popup`}>

                        <div className="d-flex flex-row justify-content-end">
                            <div className="page-icon" onClick={() => setGetMoreCredit("")}>
                                <img
                                    src="/images/close-inactive.png"
                                    alt="close"
                                    className="icon-normal"
                                />
                                <img
                                    src="/images/close-active.png"
                                    alt="close"
                                    className="icon-hover"
                                />
                            </div>
                        </div>

                        <div className="popup-content">
                            <h5 style={{ color: "red" }}>Ooops!</h5>

                            {getMoreCredit && (
                                <p className="popup-message-modern">
                                    {getMoreCredit}<br /> <span style={{ fontSize: "12px", fontWeight: 100, color: "blue", cursor: "pointer" }} onClick={() => { setShowCreditForm(true); setGetMoreCredit("") }}>Get More Credits?</span>
                                </p>
                            )}
                            <button className="popup-primary-btn mt-3" onClick={() => setGetMoreCredit("")}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showCreditForm && (
                <div className="download-popup-overlay">
                    <div className="download-popup" >
                        <div className="d-flex justify-content-end">
                            <img
                                src="/images/close-active.png"
                                alt="close"
                                style={{ cursor: "pointer" }}
                                onClick={() => { setShowCreditForm(false); setCredit(""); }}
                            />
                        </div>

                        <div className="text-center">
                            <div className="mt-2">Kindly specify the number of credits<br /> required.</div>

                            <div className="download-wrapper mt-4" style={{ display: "flex", justifyContent: "space-around" }}>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    className="dashboard-input"
                                    style={{ border: "1px solid #115ea3", width: "auto" }}
                                    value={credit}
                                    placeholder="Enter number of credits"
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (/^\d*$/.test(value)) {
                                            setCredit(value);
                                        }
                                    }}
                                />
                                <button
                                    className="download"
                                    disabled={requestCreditsLoading || !credit || Number(credit) <= 0}
                                    style={{ borderRadius: "4px", cursor: requestCreditsLoading || !credit || Number(credit) <= 0 ? "not-allowed" : "pointer", backgroundColor: requestCreditsLoading || !credit || Number(credit) <= 0 ? "#888888" : "#115ea3" }}
                                    onClick={() => {
                                        submitRequest(credit); setCredit("");
                                    }}
                                >
                                    {requestCreditsLoading ? <><span style={{ fontSize: "10px" }} className="spinner-border spinner-border-sm me-2" />Processing...</> : "Submit"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showCreditPopup && (
                <div className="avail-popup-overlay">
                    <div className="avail-popup">
                        <div className="d-flex flex-row justify-content-end">
                            <div
                                className="page-icon"
                                onClick={() => setShowCreditPopup(false)}
                            >
                                <img
                                    src="/images/close-inactive.png"
                                    alt="normal"
                                    className="icon-normal"
                                />
                                <img
                                    src="/images/close-active.png"
                                    alt="hover"
                                    className="icon-hover"
                                />
                            </div>
                        </div>

                        <div className='text-center'>
                            <img src="/images/hand-shak.png" alt="" />
                            <p className="popup-title">Thank you</p>
                            <p className="popup-text">
                                Your request has been submitted successfully.
                                Our <br />BDA will get in touch with you soon.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SearchPage;
